import { cx } from '../internal/cx';
import { devWarn } from '../internal/dev-warn';
import { brandAssets } from '../../assets';

/**
 * Só duas variantes existem porque só dois assets existem. `docs/product/brand/BRAND-EXTRACT.md`
 * §12 é explícito: não há SVG master no repo, e "enquanto não houver SVG master, não
 * recriar o lockup em código". Por isso:
 *
 *  - `lockup-light` / `lockup-dark` — os PNGs canônicos (≥320px, brandbook §8).
 *  - `wordmark` — NÃO é um recorte da imagem (isso seria redesenhar o lockup à mão,
 *    proibido sem o master). É um tratamento tipográfico independente ("ARGINK" na
 *    stack Bahnschrift), para espaços abaixo de 320px onde o lockup não cabe.
 *
 * O símbolo A-nó (favicon/app icon, brandbook §8) **não tem asset e não é
 * oferecido aqui** — não existe variante para ele; usar `ProofSeal` ou recortar o
 * lockup manualmente para esse fim é proibido (ver `assets/brand/PROVENANCE.md`).
 */
export type BrandMarkVariant = 'lockup-light' | 'lockup-dark' | 'wordmark';

export interface BrandMarkProps {
  variant?: BrandMarkVariant;
  /** Texto alternativo da imagem — default "ARGINK". Só se aplica às variantes de lockup. */
  alt?: string;
  /** Largura renderizada em px. Abaixo de 320 nas variantes de lockup dispara aviso
   * de dev (brandbook §8) recomendando `variant="wordmark"`. */
  width?: number;
  className?: string;
}

const LOCKUP_MIN_WIDTH = 320;

export function BrandMark({ variant = 'lockup-light', alt = 'ARGINK', width, className }: BrandMarkProps) {
  const isLockup = variant === 'lockup-light' || variant === 'lockup-dark';

  if (isLockup && width !== undefined && width < LOCKUP_MIN_WIDTH) {
    devWarn(
      `BrandMark variant="${variant}" está sendo renderizado a ${width}px, abaixo do mínimo de ` +
        `${LOCKUP_MIN_WIDTH}px do lockup narrativo (BRAND-EXTRACT §8). Use variant="wordmark" em ` +
        'espaços menores — não redimensione o lockup para caber.',
    );
  }

  if (variant === 'wordmark') {
    return (
      <span
        className={cx('argink-brandmark', 'argink-brandmark--wordmark', className)}
        role="img"
        aria-label={alt}
      >
        ARGINK
      </span>
    );
  }

  const src = variant === 'lockup-dark' ? brandAssets.lockupOnDark : brandAssets.lockupOnLight;

  return (
    <img
      src={src}
      alt={alt}
      width={width}
      className={cx('argink-brandmark', 'argink-brandmark--lockup', className)}
    />
  );
}
